import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import LocationLocal from './components/LocationLocal';
import LocationState from './components/LocationState';
import OfferClaimPrice from '../OfferClaimPrice';
import Map from './components/Map';
import OfferSaveActions from '../OfferSaveActions';
import WizardBackButton from '../WizardBackButton';
// import OfferWizard from '../OfferWizard'

const locationTypes = {
  global: 'GLOBAL',
  country: 'US',
  state: 'state',
  local: 'local',
};

const isLocationValid = (locationType, location) => (
  (locationType === locationTypes.global && Boolean(location.type))
  || (locationType === locationTypes.country && Boolean(location.type))
  || (locationType === locationTypes.state && Boolean(location.states?.length))
  || (locationType === locationTypes.local && Boolean(location.local?.length))
);

const OfferAudience = ({
  back,
  onComplete,
  values,
  onSave,
  onUpdate,
  onValuesChange,
  saveButtonLabel,
}) => {
const rawAudience = values.audience || values.offerLocation || { type: 'US' };

const [locationType, setLocationType] = useState(() =>
  rawAudience.type ||
  (rawAudience.local?.length > 0 && locationTypes.local) ||
  locationTypes.country
);
const [location, setLocation] = useState(rawAudience);

  const [error, setError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isFormValid = useCallback(
    () => isLocationValid(locationType, location),
    [locationType, location]
  );

  useEffect(() => {
    if (error) {
      setError(!isFormValid());
    }
  }, [error, isFormValid]);

useEffect(() => {
  if (values?.audience) {
    const audience = values.audience;

    // Detect actual type if `type` is not passed
    const detectedType = audience.type
      || (audience.local?.length > 0 && locationTypes.local)
      || (audience.states?.length > 0 && locationTypes.state)
      || (audience.type === 'GLOBAL' && locationTypes.global)
      || locationTypes.country; // fallback

    setLocationType(detectedType);
    setLocation({ ...audience, type: detectedType });
  }
}, [values?.audience]);

  const handleChange = (e) => {
    const val = e.target.value;
    setIsDirty(true);
    let nextLocation;
    if (val === locationTypes.global) {
      nextLocation = { type: locationTypes.global };
    } else if (val === locationTypes.country) {
      nextLocation = { type: locationTypes.country };
    } else if (val === locationTypes.local) {
      nextLocation = {
        local: location.local?.length ? location.local : [],
        type: "local",
      };
    } else {
      nextLocation = {
        states: [],
        type: locationTypes.state,
      };
    }
    setLocation(nextLocation);
    setError(!isLocationValid(val, nextLocation));
    setLocationType(val);
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      setError(true);
      return;
    }

     const transformedLocation = {
  type: locationType, // explicitly include type
  ...(locationType === locationTypes.local && { local: location.local }),
  ...(locationType === locationTypes.state && { states: location.states }),
  id: {
    regionType: locationType,
    regionValue: "ALL",
  },
};

 // delete transformedLocation.type; // optional: remove redundant 'type'

    // 👇 Send transformed object
    onComplete(transformedLocation);

    // Submit the location directly with all selected locations
    //onComplete(location);
  };

  const getAudienceValue = useCallback(() => ({
    type: locationType,
    ...(locationType === locationTypes.local && { local: location.local }),
    ...(locationType === locationTypes.state && { states: location.states }),
    ...(locationType === locationTypes.global && { type: locationTypes.global }),
  }), [location, locationType]);

  useEffect(() => {
    onValuesChange?.('audience', getAudienceValue(), isDirty);
  }, [getAudienceValue, isDirty, onValuesChange]);

  const handleUpdate = () => {
    if (!isFormValid()) {
      setError(true);
      return;
    }
    onUpdate?.({ audience: getAudienceValue() });
  };

  return (
    <div>
      <Box mb={4}>
        <Typography variant="h6" component="h2">
          Where are your customers?
        </Typography>
        <Typography variant="body2" component="p" color="textSecondary">
          Find new customers in the areas you serve
        </Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid container item lg={5} sm={12}>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" error={false}>
              <FormLabel component="legend">Designate the area you want to cover</FormLabel>
              <RadioGroup onChange={handleChange} value={locationType}>
                <FormControlLabel
                  value={locationTypes.global}
                  control={<Radio />}
                  label="Global"
                />
                <FormControlLabel
                  value={locationTypes.country}
                  control={<Radio />}
                  label="United States"
                />
                <FormControlLabel
                  value={locationTypes.state}
                  control={<Radio />}
                  label="Specific state(s)"
                />
                <FormControlLabel
                  value={locationTypes.local}
                  control={<Radio />}
                  label="A radius around your business(es)"
                />
              </RadioGroup>
            </FormControl>
            <Box mt={2}>
              {[locationTypes.global, locationTypes.country].includes(locationType) && (
                <OfferClaimPrice regionType={locationType} />
              )}
              {locationType === locationTypes.state && (
                <LocationState
                  error={error}
                  value={location.states}
                  updateLocation={(val) => {
                    const nextLocation = {
                      states: val,
                      type: locationTypes.state,
                    };
                    setIsDirty(true);
                    setLocation(nextLocation);
                    setError(!isLocationValid(locationTypes.state, nextLocation));
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid item lg={7} xs={12} style={{ marginBottom: 20 }}>
          <Map
            locationType={locationType}
            location={
              locationType === locationTypes.local
                ? location.local // Pass the array of local locations
                : location
            }
          />
        </Grid>
        {locationType === locationTypes.local && (
          <Grid item xs={12}>
            <LocationLocal
              error={error}
              value={location.local}
              updateLocation={(val) => {
                const nextLocation = {
                  local: val,
                  type: locationTypes.local,
                };
                setIsDirty(true);
                setLocation(nextLocation);
                setError(!isLocationValid(locationTypes.local, nextLocation));
              }}
            />
          </Grid>
        )}
      </Grid>
      <Box mt={2} mb={1} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <OfferSaveActions
          onUpdate={onUpdate ? handleUpdate : undefined}
          onSave={() => onSave({ audience: getAudienceValue() })}
          saveButtonLabel={saveButtonLabel}
        />
        <Box>
          <WizardBackButton onClick={back} />
          <Button
            color="primary"
            onClick={handleSubmit}
            variant="contained"
            type="submit"
          >
            Next
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default OfferAudience;
